import React, {useContext, useRef, useState, useEffect} from "react";
import {
    Input,
    Row,
    Col,
    CardHeader,
    Card,
    CardBody,
    Button,
    FormGroup,
    Label
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import { UserLoginContext } from "../../context/UserLoginContext";
import { EventsContext } from "../../context/EventsContext";
import {useHistory, useParams} from "react-router-dom";
import SweetAlert from "react-bootstrap-sweetalert";
import rest from "../../API/api";
import { authenticatedFetch } from "../../services/authService";

const ProcessRegistration = () => {
    const {id} = useParams(); // Registration ID
    const [userState, setUserState] = useContext(UserLoginContext);
    const [eventsState, setEventsState] = useContext(EventsContext);
    const notificationAlertRef = useRef(null);
    const history = useHistory();

    // Registration data
    const [registration, setRegistration] = useState(null);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);

    // Editable fields
    const [notes, setNotes] = useState("");
    const [processed, setProcessed] = useState(false);
    const [alert, setAlert] = useState(null);

    // Load registration and event data
    useEffect(() => {
        if (userState.status && id) {
            loadRegistration();
        }
    }, [userState.status, id]);

    const loadRegistration = async () => {
        try {
            // Load the unprocessed registration
            const regResponse = await authenticatedFetch(`${rest.unprocessedregistrations}/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            });

            if (!regResponse.ok) {
                notify("tc", "danger", "Failed to load registration");
                return;
            }

            const regData = await regResponse.json();
            setRegistration(regData);
            setNotes(regData.Notes || "");
            setProcessed(regData.Processed || false);

            // Load the associated event
            if (regData.EventId) {
                const eventResponse = await authenticatedFetch(`${rest.events}/${regData.EventId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                });

                if (eventResponse.ok) {
                    const eventData = await eventResponse.json();
                    setEvent(eventData);
                }
            }

            setLoading(false);
        } catch (error) {
            console.error('Error loading registration:', error);
            notify("tc", "danger", "Error loading registration");
            setLoading(false);
        }
    };

    const notify = (place, color, message) => {
        // Check if ref is initialized before calling
        if (!notificationAlertRef.current) {
            console.warn('Notification ref not ready:', message);
            return;
        }

        var type = color;
        var options = {
            place: place,
            message: (
                <div>
                    <div>{message}</div>
                </div>
            ),
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 3
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    const processRegistrationConfirm = () => {
        setAlert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, process it!"
                confirmBtnBsStyle="success"
                cancelBtnBsStyle="default"
                title="Process Registration?"
                style={{display: "block"}}
                onConfirm={() => processRegistration()}
                onCancel={() => setAlert(null)}
            >
                This will add the registration to the event and mark it as processed. Continue?
            </SweetAlert>
        );
    };

    const processRegistration = async () => {
        setAlert(null);

        try {
            // Add registration to the event's Registrations array
            const addRegResponse = await authenticatedFetch(`${rest.events}/${registration.EventId}/registrations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                },
                body: JSON.stringify({
                    EventId: registration.EventId,
                    SignedWaiver: registration.SignedWaiver,
                    Name: registration.Name,
                    Email: registration.Email,
                    DOB: registration.DOB,
                    Gender: registration.Gender,
                    Phone: registration.Phone,
                    Questions: registration.Questions || [],
                    CustomerXRef: registration.CustomerXRef || "",
                    Processed: true,
                    CCNumber: registration.CCNumber || "",
                    CCName: registration.CCName || "",
                    CCExpiry: registration.CCExpiry || "",
                    CCCvc: registration.CCCvc || "",
                    CCIssuer: registration.CCIssuer || "",
                    CCZip: registration.CCZip || "",
                    Notes: notes
                })
            });

            if (!addRegResponse.ok) {
                notify("tc", "danger", "Failed to add registration to event");
                return;
            }

            // Delete the unprocessed registration
            const deleteResponse = await authenticatedFetch(`${rest.unprocessedregistrations}/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            });

            if (!deleteResponse.ok) {
                notify("tc", "warning", "Registration added but failed to delete unprocessed record");
            }

            // Reload events to update the context
            const eventsResponse = await authenticatedFetch(rest.events, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            });

            if (eventsResponse.ok) {
                const eventsData = await eventsResponse.json();
                setEventsState(prevState => ({
                    ...prevState,
                    theEvents: eventsData
                }));
            }

            notify("tc", "success", "Registration processed successfully");

            // Navigate back to unprocessed registrations table
            setTimeout(() => {
                history.push("/admin/unprocessedregistrations");
            }, 1500);

        } catch (error) {
            console.error('Error processing registration:', error);
            notify("tc", "danger", "Error processing registration");
        }
    };

    const handleCancel = () => {
        history.push("/admin/unprocessedregistrations");
    };

    if (loading) {
        return (
            <div className="content">
                <Row>
                    <Col md={12}>
                        <Card>
                            <CardBody>
                                <p>Loading registration...</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }

    if (!registration) {
        return (
            <div className="content">
                <Row>
                    <Col md={12}>
                        <Card>
                            <CardBody>
                                <p>Registration not found</p>
                                <Button color="info" onClick={handleCancel}>Back to List</Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <>
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <div className="content">
                <Row>
                    <Col md={12}>
                        <Card>
                            <CardHeader>
                                <h5 className="title">Process Event Registration</h5>
                                <p className="category">Review and approve registration to add it to the event</p>
                            </CardHeader>
                            <CardBody>
                                {/* Event Information */}
                                {event && (
                                    <Row>
                                        <Col md={12}>
                                            <h6 className="title">Event Information</h6>
                                            <FormGroup>
                                                <Label>Event Title</Label>
                                                <Input
                                                    type="text"
                                                    value={event.Title || ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                            <FormGroup>
                                                <Label>Event Type</Label>
                                                <Input
                                                    type="text"
                                                    value={event.Type || ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                            <FormGroup>
                                                <Label>Event Date</Label>
                                                <Input
                                                    type="text"
                                                    value={event.StartDate ? new Date(event.StartDate).toLocaleDateString() : ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                            <FormGroup>
                                                <Label>Current Registrations / Max</Label>
                                                <Input
                                                    type="text"
                                                    value={`${event.Registrations?.length || 0} / ${event.MaxRegistrations}`}
                                                    disabled
                                                />
                                            </FormGroup>
                                            <hr />
                                        </Col>
                                    </Row>
                                )}

                                {/* Registrant Information */}
                                <Row>
                                    <Col md={12}>
                                        <h6 className="title">Registrant Information</h6>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Name <span className="text-danger">*</span></Label>
                                            <Input
                                                type="text"
                                                value={registration.Name || ""}
                                                disabled
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Email <span className="text-danger">*</span></Label>
                                            <Input
                                                type="email"
                                                value={registration.Email || ""}
                                                disabled
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Phone</Label>
                                            <Input
                                                type="text"
                                                value={registration.Phone || ""}
                                                disabled
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Date of Birth</Label>
                                            <Input
                                                type="text"
                                                value={registration.DOB || ""}
                                                disabled
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Gender</Label>
                                            <Input
                                                type="text"
                                                value={registration.Gender || ""}
                                                disabled
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Waiver Signed</Label>
                                            <Input
                                                type="text"
                                                value={registration.SignedWaiver ? "Yes" : "No"}
                                                disabled
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {/* Additional Questions */}
                                {registration.Questions && registration.Questions.length > 0 && (
                                    <Row>
                                        <Col md={12}>
                                            <hr />
                                            <h6 className="title">Additional Questions</h6>
                                        </Col>
                                        {registration.Questions
                                            .filter(q => q != null && q !== undefined)
                                            .map((q, index) => (
                                            <Col md={12} key={index}>
                                                <FormGroup>
                                                    <Label>{q.Title || q.Question}</Label>
                                                    <Input
                                                        type="text"
                                                        value={q.Value || ""}
                                                        disabled
                                                    />
                                                </FormGroup>
                                            </Col>
                                        ))}
                                    </Row>
                                )}

                                {/* Payment Information (if provided) */}
                                {(registration.CCNumber || registration.CCName) && (
                                    <Row>
                                        <Col md={12}>
                                            <hr />
                                            <h6 className="title">Payment Information</h6>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Cardholder Name</Label>
                                                <Input
                                                    type="text"
                                                    value={registration.CCName || ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Card Number (Full)</Label>
                                                <Input
                                                    type="text"
                                                    value={registration.CCNumber || ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Expiration Date</Label>
                                                <Input
                                                    type="text"
                                                    value={registration.CCExpiry || ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>CVC/Security Code</Label>
                                                <Input
                                                    type="text"
                                                    value={registration.CCCvc || ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Zip Code</Label>
                                                <Input
                                                    type="text"
                                                    value={registration.CCZip || ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Card Issuer</Label>
                                                <Input
                                                    type="text"
                                                    value={registration.CCIssuer || ""}
                                                    disabled
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                )}

                                {/* Admin Notes (Editable) */}
                                <Row>
                                    <Col md={12}>
                                        <hr />
                                        <h6 className="title">Admin Notes</h6>
                                        <FormGroup>
                                            <Label>Notes (Optional)</Label>
                                            <Input
                                                type="textarea"
                                                rows="3"
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder="Add any notes about this registration..."
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {/* Action Buttons */}
                                <Row>
                                    <Col md={12}>
                                        <hr />
                                        {!processed && (
                                            <Button color="success" onClick={processRegistrationConfirm}>
                                                <i className="tim-icons icon-check-2" /> Process & Approve Registration
                                            </Button>
                                        )}
                                        {processed && (
                                            <div className="alert alert-success" role="alert">
                                                <i className="tim-icons icon-check-2" /> This registration has already been processed.
                                            </div>
                                        )}
                                        <Button color="secondary" onClick={handleCancel} className="ml-2">
                                            <i className="tim-icons icon-simple-remove" /> {processed ? 'Back to List' : 'Cancel'}
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
            {alert}
        </>
    );
};

export default ProcessRegistration;
